import type { ReactNode } from 'react';
import { cn } from '../lib/cn';

/**
 * Group of settings in a card. Used by the settings pages the kernel renders from the module
 * manifests (docs/architecture/modules.md, section Einstellungen).
 */
export function SettingsSection({
  title,
  description,
  children,
  footer,
  className,
}: {
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  className?: string;
}) {
  return (
    <section className={cn('rounded-lg border border-line bg-surface', className)}>
      <header className="px-4 pt-4 pb-1 md:px-5">
        <h2 className="text-base font-semibold text-fg">{title}</h2>
        {description && <p className="mt-0.5 text-fg-muted">{description}</p>}
      </header>
      <div className="divide-y divide-line px-4 md:px-5">{children}</div>
      {footer && (
        <div className="flex justify-end gap-2 border-t border-line bg-canvas px-4 py-3 md:px-5">
          {footer}
        </div>
      )}
    </section>
  );
}

export interface SettingsRowProps {
  label: ReactNode;
  description?: ReactNode;
  /** Id of the control, connects the label. */
  htmlFor?: string;
  /** Status next to the label, e.g. "Firmenvorgabe" or "geändert". */
  badge?: ReactNode;
  children: ReactNode;
}

/** One setting: text left, control right on wide screens, stacked on the phone. */
export function SettingsRow({ label, description, htmlFor, badge, children }: SettingsRowProps) {
  return (
    <div className="flex flex-col gap-3 py-4 md:flex-row md:items-center md:gap-6">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          {htmlFor ? (
            <label htmlFor={htmlFor} className="font-medium text-fg">
              {label}
            </label>
          ) : (
            <span className="font-medium text-fg">{label}</span>
          )}
          {badge}
        </div>
        {description && <p className="mt-0.5 text-fg-muted">{description}</p>}
      </div>
      <div className="flex shrink-0 flex-wrap items-center gap-2 md:max-w-[50%] md:min-w-56 md:justify-end">
        {children}
      </div>
    </div>
  );
}
